import { z } from "zod";

// Application permission tiers (Better Auth `user.role`). Distinct from the HR
// job-role catalog (`src/schemas/roles.ts` / person_role). Adding a tier (e.g.
// "manager") means adding it here and deciding its guard semantics — the
// dialogs, audit labels, and badges all derive from this list.
// Ordered least- to most-privileged, which is the order the role picker shows.
// `admin_readonly` is a separate value rather than a flag on `admin` because
// Better Auth grants its admin API by role name — see `src/lib/permissions.ts`.
const APP_ROLE_VALUES = ["user", "manager", "admin_readonly", "admin"] as const;

export type AppRole = (typeof APP_ROLE_VALUES)[number];

export const APP_ROLE_LABELS: Record<AppRole, string> = {
  user: "User",
  manager: "Manager",
  admin_readonly: "Admin (view only)",
  admin: "Admin",
};

/** One-line explanation per tier, shown beside the picker. */
export const APP_ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  user: "Can see the app and edit a few fields on their own profile.",
  manager: "Adds their direct reports — team page, team certifications, aspirations.",
  admin_readonly: "Can see everything, including admin pages. Cannot change anything.",
  admin: "Can see and change everything, including user accounts.",
};

export const APP_ROLES = APP_ROLE_VALUES.map((value) => ({
  value,
  label: APP_ROLE_LABELS[value],
}));

export const appRoleSchema = z.enum(APP_ROLE_VALUES);

/**
 * The Users page's default filter: every tier above plain User.
 *
 * Derived rather than written out, so adding a tier cannot silently leave its
 * holders off the default view — which would be the one place their elevated
 * access was supposed to be visible.
 */
export const ELEVATED_ROLES: string[] = APP_ROLE_VALUES.filter((r) => r !== "user");

/**
 * Narrow a DB string to a known tier. Needed because `user.role` is plain text:
 * a legacy value, or one written by a newer build, must not be smuggled into a
 * typed slot with a cast (banned by the code-quality rules).
 */
export function isAppRole(role: string): role is AppRole {
  // Compared through APP_ROLES so no cast is needed — `includes` on the
  // readonly tuple would require widening `role` to AppRole first.
  return APP_ROLES.some((r) => r.value === role);
}

/** Display label for a role value of unknown provenance (DB strings). */
export function appRoleLabel(role: string): string {
  const match = APP_ROLES.find((r) => r.value === role);
  return match ? match.label : role;
}
